import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import './DraftPages.css'

const Icons = {
  briefcase: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  mapPin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  trendingUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  heart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  coffee: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
      <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
      <line x1="6" y1="1" x2="6" y2="4" />
      <line x1="10" y1="1" x2="10" y2="4" />
      <line x1="14" y1="1" x2="14" y2="4" />
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  award: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  )
}

const whyJoinUs = [
  {
    icon: Icons.target,
    title: 'Mission-Driven Work',
    description: 'Help solve the climate crisis by maximizing clean energy production. Every line of code contributes to a sustainable future.'
  },
  {
    icon: Icons.zap,
    title: 'Cutting-Edge Technology',
    description: 'Work with AI, drones, satellite imagery, and IoT sensors. Build systems that push the boundaries of what\'s possible.'
  },
  {
    icon: Icons.trendingUp,
    title: 'Growth Opportunities',
    description: 'Join an early-stage company where your impact is visible. Shape products, processes, and culture from the ground up.'
  },
  {
    icon: Icons.globe,
    title: 'Remote-First Culture',
    description: 'Work from anywhere in Europe. We believe great talent isn\'t limited by geography.'
  }
]

const benefits = [
  { icon: Icons.award, title: 'Competitive Salary', description: 'Market-rate compensation with equity options' },
  { icon: Icons.heart, title: 'Health Benefits', description: 'Comprehensive health, dental, and vision coverage' },
  { icon: Icons.coffee, title: 'Unlimited PTO', description: 'Take the time you need to recharge' },
  { icon: Icons.globe, title: 'Remote Flexibility', description: 'Work from home or our EU offices' },
  { icon: Icons.book, title: 'Learning Budget', description: 'Annual stipend for courses and conferences' },
  { icon: Icons.users, title: 'Team Retreats', description: 'Quarterly in-person meetups across Europe' }
]

const openPositions = [
  {
    title: 'Senior Solar Engineer',
    department: 'Engineering',
    location: 'Remote (EU)',
    type: 'Full-time',
    description: 'Design and optimize solar monitoring algorithms. Deep expertise in PV systems, inverters, and energy production analysis.'
  },
  {
    title: 'Machine Learning Engineer',
    department: 'Data Science',
    location: 'San Francisco, CA',
    type: 'Full-time',
    description: 'Build predictive maintenance models using thermal imaging, sensor data, and satellite imagery. Python, TensorFlow, time-series analysis.'
  },
  {
    title: 'Drone Operations Specialist',
    department: 'Field Operations',
    location: 'Austin, TX',
    type: 'Full-time',
    description: 'Lead aerial thermal inspections of solar installations. FAA Part 107 certified. Experience with DJI drones and FLIR cameras.'
  },
  {
    title: 'Full Stack Developer',
    department: 'Engineering',
    location: 'Remote (EU)',
    type: 'Full-time',
    description: 'Build our React dashboard and Node.js APIs. Experience with real-time data, PostgreSQL, and cloud infrastructure (AWS/GCP).'
  },
  {
    title: 'Product Manager',
    department: 'Product',
    location: 'New York, NY',
    type: 'Full-time',
    description: 'Define product roadmap for our monitoring platform. Work closely with customers and engineering to ship features that matter.'
  },
  {
    title: 'Customer Success Manager',
    department: 'Customer Success',
    location: 'Remote (EU)',
    type: 'Full-time',
    description: 'Onboard solar operators and ensure they get maximum value from our platform. Technical background with excellent communication skills.'
  }
]

const departmentColors = {
  'Engineering': 'var(--color-drone)',
  'Data Science': 'var(--color-energy)',
  'Field Operations': 'var(--color-solar)',
  'Product': '#a855f7',
  'Customer Success': '#ec4899'
}

export default function Careers() {
  return (
    <div className="draft-page careers-page">
      {/* Hero */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">Join Our Team</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              Shape the Future of{' '}
              <GradientText gradient="energy">Clean Energy</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              We're on a mission to maximize solar energy production worldwide.
              Join us in building the technology that powers a sustainable future.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <div style={{ marginTop: 'var(--space-xl)' }}>
              <GlowButton href="#positions" variant="solar" size="large">
                View Open Positions
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Why Join AI4Solar?</h2>
            <p>More than a job - a chance to make a real impact</p>
          </ScrollReveal>

          <div className="values-grid">
            {whyJoinUs.map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div className="value-card" whileHover={{ y: -5 }}>
                  <span className="value-icon">{item.icon}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Benefits & Perks</h2>
            <p>We take care of our team so they can focus on what matters</p>
          </ScrollReveal>

          <div className="benefits-grid">
            {benefits.map((benefit, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <motion.div className="benefit-card" whileHover={{ y: -3 }}>
                  <span className="benefit-icon">{benefit.icon}</span>
                  <div>
                    <h4>{benefit.title}</h4>
                    <p>{benefit.description}</p>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="about-section" id="positions">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Open Positions</h2>
            <p>Find your next opportunity with us</p>
          </ScrollReveal>

          <div className="positions-list">
            {openPositions.map((position, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className="position-card"
                  whileHover={{ y: -3, borderColor: 'rgba(255, 255, 255, 0.2)' }}
                >
                  <div className="position-header">
                    <div>
                      <span
                        className="position-department"
                        style={{ color: departmentColors[position.department] }}
                      >
                        {position.department}
                      </span>
                      <h3 className="position-title">{position.title}</h3>
                    </div>
                    <Link to="/contact" className="position-apply-btn">
                      Apply
                      <span className="apply-arrow">{Icons.arrowRight}</span>
                    </Link>
                  </div>
                  <p className="position-description">{position.description}</p>
                  <div className="position-meta">
                    <span className="position-location">
                      {Icons.mapPin}
                      {position.location}
                    </span>
                    <span className="position-type">
                      {Icons.clock}
                      {position.type}
                    </span>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>Don't See Your Role?</h2>
            <p>
              We're always looking for talented people who share our mission.
              Send us your resume and tell us how you'd like to contribute.
            </p>
            <div className="cta-buttons">
              <GlowButton href="/contact" variant="solar" size="large">
                Get in Touch
              </GlowButton>
              <GlowButton href="/about" variant="outline" size="large">
                Learn About Us
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <style>{`
        /* Benefits Grid */
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-lg);
        }

        .benefit-card {
          display: flex;
          align-items: flex-start;
          gap: var(--space-md);
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-xl);
          padding: var(--space-lg);
          transition: all var(--transition-base);
        }

        .benefit-card:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .benefit-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          min-width: 40px;
          background: rgba(255, 215, 0, 0.1);
          border-radius: var(--radius-md);
          color: var(--color-solar);
        }

        .benefit-icon svg {
          width: 20px;
          height: 20px;
        }

        .benefit-card h4 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-white);
          margin-bottom: var(--space-xs);
        }

        .benefit-card p {
          font-size: 0.875rem;
          color: var(--color-gray-400);
          line-height: 1.5;
        }

        /* Positions List */
        .positions-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-lg);
          max-width: 900px;
          margin: 0 auto;
        }

        .position-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-xl);
          padding: var(--space-xl);
          transition: all var(--transition-base);
        }

        .position-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--space-md);
        }

        .position-department {
          display: inline-block;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: var(--space-xs);
        }

        .position-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--color-white);
        }

        .position-description {
          font-size: 0.9375rem;
          color: var(--color-gray-400);
          line-height: 1.6;
          margin-bottom: var(--space-lg);
        }

        .position-meta {
          display: flex;
          gap: var(--space-xl);
        }

        .position-location,
        .position-type {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          font-size: 0.875rem;
          color: var(--color-gray-500);
        }

        .position-location svg,
        .position-type svg {
          width: 16px;
          height: 16px;
        }

        .position-apply-btn {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm) var(--space-lg);
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(0, 255, 136, 0.1) 100%);
          border: 1px solid rgba(255, 215, 0, 0.3);
          border-radius: var(--radius-full);
          color: var(--color-solar);
          font-size: 0.875rem;
          font-weight: 600;
          text-decoration: none;
          transition: all var(--transition-base);
          white-space: nowrap;
        }

        .position-apply-btn:hover {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(0, 255, 136, 0.2) 100%);
          border-color: var(--color-solar);
          transform: translateX(3px);
        }

        .apply-arrow {
          display: flex;
          align-items: center;
        }

        .apply-arrow svg {
          width: 16px;
          height: 16px;
          transition: transform var(--transition-fast);
        }

        .position-apply-btn:hover .apply-arrow svg {
          transform: translateX(3px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .benefits-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .position-header {
            flex-direction: column;
            gap: var(--space-md);
          }

          .position-apply-btn {
            align-self: flex-start;
          }

          .position-meta {
            flex-direction: column;
            gap: var(--space-sm);
          }
        }

        @media (max-width: 640px) {
          .benefits-grid {
            grid-template-columns: 1fr;
          }

          .values-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}
